import { memo, useMemo } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Globe, Users2, TrendingUp } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCommunityStats } from "@/hooks/use-community-stats";
import Tile from "../shared/Tile";
import TileGrid from "../shared/TileGrid";
import SectionTitle from "../layout/SectionTitle";
import TestimonialCard from "../shared/TestimonialCard";
import AvatarScroll from "./AvatarScroll";
import { theme } from "@/theme";

const testimonials = [
  {
    name: "Maria Santos",
    location: "São Paulo, Brazil",
    text: "Klau y Ros transformed my understanding of connection in dance. Their teaching style is both technical and deeply emotional. I've never felt more confident on the dance floor.",
  },
  {
    name: "James Chen",
    location: "Singapore",
    text: "The musicality course opened my eyes to a whole new dimension of Bachata. Every lesson is structured perfectly, and the community support is incredible.",
  },
  {
    name: "Sofia Rodriguez",
    location: "Madrid, Spain",
    text: "As a professional dancer, I thought I knew it all. But Klau y Ros showed me there's always room to grow. Their connection mastery course is phenomenal.",
  },
] as const;

const Community = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-50px" });
  const isMobile = useIsMobile();
  const communityStats = useCommunityStats();

  // Scroll-based animations (only on desktop)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Memoize spring config for performance
  const springConfig = useMemo(() => ({
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  }), []);

  const smoothProgress = useSpring(scrollYProgress, springConfig);

  // Memoize transform ranges for performance
  const transformRanges = useMemo(() => ({
    y: isMobile ? [0, 0] : [100, -100],
    yFast: isMobile ? [0, 0] : [150, -150],
    opacity: isMobile ? [1, 1, 1, 1] : [0, 1, 1, 0],
    scale: isMobile ? [1, 1, 1] : [0.8, 1, 0.8],
    rotate: isMobile ? [0, 0] : [0, 360],
  }), [isMobile]);

  // Parallax transforms (disabled on mobile)
  const y = useTransform(smoothProgress, [0, 1], transformRanges.y);
  const yFast = useTransform(smoothProgress, [0, 1], transformRanges.yFast);
  const opacity = useTransform(smoothProgress, [0, 0.2, 0.8, 1], transformRanges.opacity);
  const scale = useTransform(smoothProgress, [0, 0.5, 1], transformRanges.scale);
  const rotate = useTransform(smoothProgress, [0, 1], transformRanges.rotate);

  return (
    <section ref={ref} className="relative pt-8 md:pt-12 pb-8 px-4 overflow-hidden">
      {/* Enhanced scroll-driven particles */}
      <div className="absolute inset-0 opacity-10 pointer-events-none hidden md:block">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? theme.colors.accent : theme.colors.accentLight,
              y: i % 3 === 0 ? y : yFast,
              rotate: rotate,
              opacity: opacity,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Floating gradient orbs - hidden on mobile */}
      {!isMobile && (
        <>
          <motion.div
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${theme.colors.accent}, transparent)`,
              y: yFast,
              scale: scale,
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${theme.colors.accentLight}, transparent)`,
              y: y,
              scale: scale,
            }}
          />
        </>
      )}

      <div className="relative max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 20 : 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ 
            duration: isMobile ? 0.5 : 1, 
            ease: [0.22, 1, 0.36, 1],
            type: isMobile ? "tween" : "spring",
            stiffness: 60
          }}
          style={{ 
            y: isMobile ? 0 : y,
          }}
          className="mb-16"
        >
          <SectionTitle
            title="Our Community"
            accent="Community"
            subtitle="Join thousands of dancers who have transformed their journey with us"
            align="center"
            size="lg"
          />
        </motion.div>

        {/* Testimonials with basic TestimonialCard */}
        <TileGrid columns={3} gap="lg">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ 
                opacity: 0, 
                y: 30,
              }}
              whileInView={{ 
                opacity: 1, 
                y: 0,
              }}
              viewport={{ once: false, margin: "-50px", amount: 0.3 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.15,
                ease: [0.25, 0.1, 0.25, 1]
              }}
              className="h-full"
            >
              <TestimonialCard
                name={testimonial.name}
                location={testimonial.location}
                text={testimonial.text}
              />
            </motion.div>
          ))}
        </TileGrid>

        {/* Active Community with scrolling avatars */}
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 20 : 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ 
            duration: isMobile ? 0.5 : 0.8, 
            delay: isMobile ? 0.3 : 0.8,
          }}
          className="mt-20 text-center"
        >
          <div 
            className="text-2xl md:text-3xl font-bold mb-8"
            style={{ color: theme.colors.accent }}
          >
            Active Community
          </div>

          {/* Avatar Scroll Component */}
          <AvatarScroll />

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-12 flex flex-col md:flex-row gap-8 justify-center items-center"
          >
            <div className="text-center">
              <div 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: theme.colors.accent }}
              >
                {communityStats.loading ? "..." : `${communityStats.members.toLocaleString()}+`}
              </div>
              <div style={{ color: theme.colors.textMuted }}>Active Members</div>
            </div>
            <div 
              className="hidden md:block w-px h-12" 
              style={{ backgroundColor: `${theme.colors.border}` }}
            />
            <div className="text-center">
              <div 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: theme.colors.accent }}
              >
                24/7
              </div>
              <div style={{ color: theme.colors.textMuted }}>Community Support</div>
            </div>
            <div 
              className="hidden md:block w-px h-12" 
              style={{ backgroundColor: `${theme.colors.border}` }}
            />
            <div className="text-center">
              <div 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: theme.colors.accent }}
              >
                {communityStats.loading ? "..." : `${communityStats.countries}+`}
              </div>
              <div style={{ color: theme.colors.textMuted }}>Countries</div>
            </div>
          </motion.div>

          {/* Join Now CTA */}
          <Link to="/pricing">
            <motion.button
              whileHover={!isMobile ? { 
                scale: 1.1,
                y: -5,
              } : { scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.5, 
                delay: isMobile ? 0.4 : 1.2,
                type: isMobile ? "tween" : "spring",
                stiffness: 400,
                damping: 10
              }}
              className="theme-button-primary rounded-full relative overflow-hidden mt-10"
              style={{
                padding: '14px 36px',
                fontSize: '16px',
                fontWeight: 600,
                boxShadow: `0 0 20px ${theme.colors.accent}50`
              }}
            >
              <motion.span 
                className="relative z-10"
                animate={!isMobile ? {
                  x: [0, 3, 0],
                } : {}}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                Join Now →
              </motion.span>
            
            {/* Ripple effect - desktop only */}
            {!isMobile && (
              <motion.div
                className="absolute inset-0 rounded-full"
                initial={{ scale: 0, opacity: 0.5 }}
                whileHover={{
                  scale: 1.5,
                  opacity: 0,
                }}
                transition={{ duration: 0.6 }}
                style={{
                  background: `radial-gradient(circle, ${theme.colors.accent}66, transparent)`
                }}
              />
            )}
            </motion.button>
          </Link>
        </motion.div>

        {/* Features Grid - Using new Tile components */}
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 20 : 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ 
            duration: isMobile ? 0.5 : 1, 
            delay: isMobile ? 0.5 : 1.2,
            type: isMobile ? "tween" : "spring",
            stiffness: 60
          }}
          className="mt-16"
        >
          <TileGrid columns={3} gap="lg">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0 }}
            >
              <Tile
                title="Learn Anywhere, Anytime"
                description="Access our full library of Endless Bachata classes 24/7 from any device. Train at your own pace, whether you're at home, in the studio, or on the go."
                icon={Globe}
                variant="glass"
                hoverable
                spotlight
                badge="24/7"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <Tile
                title="Guided by Klau & Ros"
                description="Learn directly from international artists Klau & Ros with step-by-step explanations, technique breakdowns, and tips to improve your style and connection."
                icon={Users2}
                variant="elevated"
                hoverable
                spotlight
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Tile
                title="Specialized Courses for Every Level"
                description="From fundamentals to advanced combos, partnerwork, and lady and men style, our structured programs are designed to help you progress and shine on the dance floor."
                icon={TrendingUp}
                variant="gradient"
                hoverable
                spotlight
              />
              </motion.div>
          </TileGrid>
        </motion.div>
      </div>
    </section>
  );
};

export default memo(Community);
